import axios from "axios";
import Config from "../_config/config";

export interface UsageSummaryParams {
  startDate?: string;
  endDate?: string;
  feature?: string;
  groupBy?: string;
}

export interface UserUsageParams {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface QuotaData {
  id?: number;
  quotaType: string;
  userId?: number | null;
  feature?: string | null;
  maxRequests?: number | null;
  maxTokens?: number | null;
  /**
   * Monthly $ spend cap. Strongest billing guardrail — a token-only cap can
   * be blown by switching a feature from Haiku to Opus (~75× $/token).
   */
  maxEstimatedCostUsd?: number | null;
  period: string;
}

class AiUsageService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/ai-usage`;
  }

  private getAuthHeaders(): any {
    const headers: any = { "Content-Type": "application/json" };
    const currentUser = localStorage.getItem("currentUser");
    if (currentUser && currentUser !== "undefined") {
      try {
        const user = JSON.parse(currentUser);
        if (user.token) {
          headers["Authorization"] = `Bearer ${user.token}`;
        }
      } catch (error) {
        console.error("Failed to parse currentUser:", error);
      }
    }
    return headers;
  }

  async getUsageSummary(params?: UsageSummaryParams): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await axios.get(`${this.baseUrl}/summary`, {
        headers: this.getAuthHeaders(),
        params,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch usage summary",
      };
    }
  }

  async getDailyTrend(params?: { startDate?: string; endDate?: string }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await axios.get(`${this.baseUrl}/summary`, {
        headers: this.getAuthHeaders(),
        params: { ...params, groupBy: "day" },
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch daily trend",
      };
    }
  }

  async getUserUsageDetails(params?: UserUsageParams): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await axios.get(`${this.baseUrl}/users`, {
        headers: this.getAuthHeaders(),
        params,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch user usage details",
      };
    }
  }

  async getTenantMonthlyUsage(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await axios.get(`${this.baseUrl}/tenant-monthly`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch tenant monthly usage",
      };
    }
  }

  async getQuotas(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await axios.get(`${this.baseUrl}/quotas`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch quotas",
      };
    }
  }

  async upsertQuota(quota: QuotaData): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await axios.post(`${this.baseUrl}/quotas`, quota, {
        headers: this.getAuthHeaders(),
      });
      return response.data as any;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to save quota",
      };
    }
  }

  async deleteQuota(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.delete(`${this.baseUrl}/quotas/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete quota",
      };
    }
  }
}

export default new AiUsageService();
